/**
 * OtisWidget — the floating Otis launcher + popup chat.
 *
 * Mounted once globally (in App.tsx) and shown on every authenticated app
 * screen (office + tech). A small bottom-right button opens a docked chat
 * panel on desktop / a full-screen sheet on mobile, both reusing <OtisChat />.
 *
 * Live-chat vibe: the first 3 times the widget becomes visible for a user, a
 * "Hi, I'm Otis!" teaser bubble pops up next to the button (tracked per-user in
 * localStorage). After that it stays quiet — just the button.
 *
 * Visibility: only for signed-in users, and only on routes that sit inside
 * <AuthGuard> — see `otisRoutes.ts`, which is a whitelist precisely so a new
 * public route can never inherit the widget by default.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { useTenant } from '@/hooks/useTenant';
import { OtisChat, OtisAvatar, OtisFace } from './OtisChat';
import { useOtisSuppressed } from './OtisVisibilityContext';
import { isOtisRoute } from './otisRoutes';

const TEASER_MAX_SHOWS = 3;

export function OtisWidget() {
  const { pathname } = useLocation();
  const { tenant } = useTenant();
  const companyName = tenant?.companyName || 'the company';

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false); // full-screen toggle (desktop)
  const [mounted, setMounted] = useState(false); // keep the chat alive once opened
  const [showTeaser, setShowTeaser] = useState(false);
  const teaserStartedRef = useRef(false);

  // Signed-in check is re-read on every render (route changes drive re-renders,
  // so login/logout flips this without a reactive store).
  const authed = !!localStorage.getItem('logged_in_user');
  // A focused surface (e.g. the tech job-detail overlay) can ask Otis to step
  // out of the way while it's open — see useSuppressOtis.
  const suppressed = useOtisSuppressed();
  const visible = authed && isOtisRoute(pathname) && !suppressed;

  // Open keeps the chat mounted so the conversation survives close/reopen.
  useEffect(() => {
    if (open) {
      setMounted(true);
      setShowTeaser(false);
    }
  }, [open]);

  // Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // First-3-times teaser — fires once, the first time the widget is visible.
  useEffect(() => {
    if (!visible || teaserStartedRef.current) return;
    teaserStartedRef.current = true;

    const userId = localStorage.getItem('user_id') || 'anon';
    const key = `otis_intro_count_${userId}`;
    const count = Number(localStorage.getItem(key) || '0');
    if (count >= TEASER_MAX_SHOWS) return;

    const showTimer = setTimeout(() => {
      setShowTeaser(true);
      localStorage.setItem(key, String(count + 1));
    }, 1500);
    const hideTimer = setTimeout(() => setShowTeaser(false), 12_000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      {/* Teaser bubble (first few visits) */}
      <AnimatePresence>
        {showTeaser && !open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="fixed bottom-[5.75rem] right-4 sm:right-5 z-40 w-[270px] max-w-[calc(100vw-2rem)]"
          >
            <button
              onClick={() => setOpen(true)}
              className="block w-full text-left rounded-2xl rounded-br-md bg-surface-card border border-theme shadow-xl px-3.5 py-3 hover:border-amber-500/40 transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <OtisAvatar size="sm" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-theme-primary">Hi, I'm Otis! 👋</p>
                  <p className="text-xs text-theme-tertiary mt-0.5 leading-snug">
                    Ask me anything about {companyName}'s history — jobs, customers, old notes, the lot.
                  </p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setShowTeaser(false)}
              aria-label="Dismiss"
              className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-surface-card border border-theme flex items-center justify-center text-theme-muted hover:text-theme-primary shadow"
            >
              <X className="w-3 h-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel — mounted once opened, animated in/out, conversation persists */}
      {mounted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={open ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.96, y: 10 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          style={{ pointerEvents: open ? 'auto' : 'none' }}
          aria-hidden={!open}
          className={
            expanded
              ? 'fixed z-50 inset-0 sm:inset-4 bg-surface sm:rounded-2xl sm:border sm:border-theme shadow-2xl flex flex-col overflow-hidden'
              : 'fixed z-50 inset-0 sm:inset-auto sm:bottom-[5.75rem] sm:right-5 sm:w-[400px] sm:h-[600px] sm:max-h-[calc(100vh-7rem)] sm:max-w-[calc(100vw-2.5rem)] bg-surface sm:rounded-2xl sm:border sm:border-theme shadow-2xl flex flex-col overflow-hidden'
          }
        >
          {/* Header */}
          <div className="shrink-0 flex items-center gap-2.5 px-4 py-3 border-b border-theme bg-gradient-to-r from-amber-500/10 to-transparent">
            <OtisAvatar size="sm" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-theme-primary leading-none">Otis</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <p className="text-[11px] text-theme-tertiary leading-none mt-0.5">
                {companyName}'s memory
              </p>
            </div>
            {/* Expand / collapse — desktop only (mobile is already full-screen) */}
            <button
              onClick={() => setExpanded((e) => !e)}
              aria-label={expanded ? 'Shrink' : 'Expand to full screen'}
              title={expanded ? 'Shrink' : 'Expand to full screen'}
              className="hidden sm:inline-flex shrink-0 w-8 h-8 rounded-lg items-center justify-center text-theme-muted hover:text-theme-primary hover:bg-surface-hover transition-colors"
            >
              {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-theme-muted hover:text-theme-primary hover:bg-surface-hover transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <OtisChat className="flex-1 min-h-0" />
        </motion.div>
      )}

      {/* Launcher button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.3 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close Otis' : 'Open Otis'}
        className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/30 flex items-center justify-center text-white hover:brightness-110 transition-[filter]"
      >
        {open ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <OtisFace className="w-8 h-8" />
            <span className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[var(--surface-page)]" />
          </>
        )}
      </motion.button>
    </>
  );
}

export default OtisWidget;
